/* Reusable Lexi conversation starter — drops the user into the chat with a prefilled question.
   Usage:
     <LexiTrigger prefill="Which B.Tech branch fits me?" label="Ask Lexi about branches" variant="pill" />
*/
import React from 'react';
import { Zap, ArrowRight } from 'lucide-react';
import { LEXI_ENABLED } from '../../lib/features';

const VARIANTS = {
  pill: 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-br from-teal-600 to-emerald-600 text-white text-sm font-semibold shadow-md hover:shadow-xl hover:scale-[1.02] transition-all',
  ghost: 'inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-teal-300 text-teal-700 text-sm font-semibold hover:bg-teal-50 hover:border-teal-400 transition-colors',
  card: 'group flex items-center justify-between w-full px-5 py-4 rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all',
  block: 'flex items-center justify-between w-full px-6 py-5 rounded-3xl bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700 text-white shadow-lg hover:shadow-2xl hover:scale-[1.01] transition-all',
  inline: 'inline-flex items-center gap-1.5 text-teal-700 text-sm font-semibold underline-offset-4 hover:underline',
};

const LexiTrigger = ({
  prefill,
  label = 'Ask Lexi',
  variant = 'pill',
  description = null,
  className = '',
  testId = 'lexi-trigger',
}) => {
  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('lexi:open', { detail: { prefill } }));
  };

  if (!LEXI_ENABLED) return null;

  if (variant === 'card') {
    return (
      <button onClick={handleClick} className={`${VARIANTS.card} ${className}`} data-testid={testId}>
        <div className="flex items-center gap-3 text-left">
          <span className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center shrink-0">
            <Zap size={18} fill="currentColor" />
          </span>
          <div>
            <p className="font-bold text-gray-900 text-sm">{label}</p>
            {description && <p className="text-xs text-gray-600 mt-0.5">{description}</p>}
          </div>
        </div>
        <ArrowRight className="text-teal-600 group-hover:translate-x-1 transition-transform" size={18} />
      </button>
    );
  }

  if (variant === 'block') {
    return (
      <button onClick={handleClick} className={`${VARIANTS.block} ${className}`} data-testid={testId}>
        <div className="flex items-center gap-4 text-left">
          <span className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0 ring-2 ring-white/30">
            <Zap size={20} fill="currentColor" />
          </span>
          <div>
            <p className="font-bold text-base">{label}</p>
            {description && <p className="text-xs text-white/85 mt-0.5">{description}</p>}
          </div>
        </div>
        <ArrowRight size={22} />
      </button>
    );
  }

  return (
    <button onClick={handleClick} className={`${VARIANTS[variant]} ${className}`} data-testid={testId}>
      <Zap size={14} fill="currentColor" />
      <span>{label}</span>
    </button>
  );
};

export default LexiTrigger;
